/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Fade, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { Box, IconButton } from '@mui/material';
import { Dispatch, FC, SetStateAction, useEffect, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { AddThingPersonDialog, EventLocationData, FilterField, Icon, useBoolFlag, useHistorySearch } from '../../../components';
import { ThingPersonDialogOperation } from '../../../components/assign-multiple-dialog/add-thing-person-dialog';
import { EntityJourneyListController } from '../../../components/entity-journey/entity-journey-list';
import { FilterBar, FilterDrawerContent } from '../../../components/filter';
import { HEIGHT_TO_CLOSE_DRAWER, WIDTH_TO_CLOSE_DRAWER } from '../../../constants';
import { useResponsiveText } from '../../../hooks';
import { CacheDataTypes, Maybe } from '../../../types';
import { innerText } from '../../../util';
import { useSearch } from '../../list/use-search';
import { MobileTabSelection } from '../event-history.types';
import { HistoryDrawerView } from './history-drawer-view';

interface Props {
  fields: FilterField[];
  filterDrawerOpen?: boolean;
  handleDrawerToggle: (value: boolean) => void;
  handleFilterOpen?: (value: boolean) => void;
  hoveredEventId: Maybe<string>;
  isDrawerOpen: boolean;
  isTransitioning: boolean;
  mobileTabSelection: MobileTabSelection;
  selectedEvent: Maybe<EventLocationData>;
  setHoveredEventId: Dispatch<SetStateAction<Maybe<string>>>;
  setSelectedEvent: Dispatch<SetStateAction<Maybe<EventLocationData>>>;
  storageKey: string;
}

const FullWidthButton = styled(Button)({ width: '100%' });
const StyledIconButton = styled(IconButton)({
  borderRadius: 0,
  width: '100%',
});

export const HistoryDrawerController: FC<Props> = ({
  fields,
  filterDrawerOpen,
  handleDrawerToggle,
  handleFilterOpen,
  hoveredEventId,
  isDrawerOpen,
  isTransitioning,
  mobileTabSelection,
  selectedEvent,
  setHoveredEventId,
  setSelectedEvent,
  storageKey,
}): JSX.Element => {
  const { t } = useTranslation(['common']);
  const trackFilterFlag = useBoolFlag('track-history-feature-filtering-temporary-20230627');
  const { filters, setFilters } = useSearch();
  const {
    currentEntityId,
    entityDialogOperationType,
    isEntityDialogOpen,
    setCurrentEntityId,
    setEntityDialogOperationType,
    setEntityTypeTab,
    setIsEntityDialogOpen,
  } = useHistorySearch();

  const mobileActions = (): JSX.Element => {
    if (!filterDrawerOpen) {
      return (
        <Stack direction="row" spacing={2} justifyContent="space-between" mx={2} pt={2}>
          {(() => {
            return (
              <FullWidthButton data-testid='history-add-button' onClick={() => setIsEntityDialogOpen(true)} variant="outlined">
                {t('common:component.assign-multiple-dialog.labels.add-entity')}
              </FullWidthButton>
            );
          })()}
          {trackFilterFlag && <FilterBar
            filtersLength={filters.length}
            onFilterClicked={() => handleFilterOpen?.(true)}
            sx={{
              borderWidth: '1px',
              borderColor: (theme) => theme.palette.primary.main,
              borderRadius: (theme) => theme.shape.borderRadius,
              borderStyle: 'solid',
            }}
          />}
        </Stack>
      );
    }

    return (
      <Stack direction="row" spacing={2} justifyContent="flex-end" mx={2} pt={2}>
        <IconButton data-testid="history-chevron-left-button" onClick={() => handleFilterOpen?.(false)}>
          <Icon icon="ChevronLeft" data-testid="history-chevron-left-icon" />
        </IconButton>
      </Stack>
    );
  };

  const desktopActions = (): JSX.Element => {
    return <>
      {trackFilterFlag
        && !filterDrawerOpen
        && <FilterBar
          filtersLength={filters.length}
          onFilterClicked={() => handleFilterOpen?.(true)}
          size="small"
        />
      }
      <IconButton
        data-testid="history-chevron-left-button"
        onClick={() => {
          if (filterDrawerOpen) return handleFilterOpen?.(false);
          handleDrawerToggle(false);
        }}
      >
        <Icon icon="ChevronLeft" data-testid="history-chevron-left-icon" />
      </IconButton>
    </>;
  };

  const renderAddEntityButton = (): JSX.Element => {
    if (filterDrawerOpen) return <></>;
    if (!isDrawerOpen) {
      return (
        <StyledIconButton onClick={() => setIsEntityDialogOpen(true)}>
          <Icon icon="Plus" />
        </StyledIconButton>
      );
    }
    return (
      (() => {
        const FullWidthButton = styled(Button)({ width: '100%' });
        return (
          <FullWidthButton data-testid='history-add-button' onClick={() => setIsEntityDialogOpen(true)} variant="outlined">
            {t('common:component.assign-multiple-dialog.labels.add-entity')}
          </FullWidthButton>
        );
      })()
    );
  };

  const renderOpenDrawerIcon = (): JSX.Element => (
    <StyledIconButton data-testid="history-chevron-right-button" onClick={() => handleDrawerToggle(true)}>
      <Icon icon="ChevronRight" data-testid="history-chevron-right-icon" />
    </StyledIconButton>
  );

  const drawerTitleText = filterDrawerOpen ? t('common:component.filter.heading') : t('common:common.labels.history');
  const drawerTitleStyle = useResponsiveText('1rem', innerText(drawerTitleText).length, {
    minFontSize: '0.875rem',
    maxFontSize: '1rem',
    textLengthThreshold: 15,
  });

  const renderDrawerTitle = (): JSX.Element =>
    <Typography style={drawerTitleStyle}>
      {drawerTitleText}
    </Typography>;

  const renderInvestigationContent = (): JSX.Element => {
    const shouldRenderList = !filterDrawerOpen && !isTransitioning;
    const shouldRenderFilter = filterDrawerOpen && !isTransitioning;

    return <>
      <Fade in={shouldRenderList}>
        <Box sx={{ display: shouldRenderList ? 'block' : 'none' }}>
          <EntityJourneyListController
            handleDrawerToggle={handleDrawerToggle}
            hoveredEventId={hoveredEventId}
            isDrawerOpen={isDrawerOpen}
            selectedEvent={selectedEvent}
            setHoveredEventId={setHoveredEventId}
            setSelectedEvent={setSelectedEvent}
          />
          {renderAddEntityButton()}
        </Box>
      </Fade>
      <Fade in={shouldRenderFilter}>
        <Box sx={{ display: shouldRenderFilter ? 'block' : 'none' }}>
          <FilterDrawerContent
            fields={fields}
            filters={filters}
            onFiltersChanged={setFilters}
            showToolbar={false}
            storageKey={storageKey}
          />
        </Box>
      </Fade>
    </>;
  };

  const renderAddEntityDialog = (): JSX.Element => (
    <AddThingPersonDialog
      closeDialog={() => {
        setEntityDialogOperationType(ThingPersonDialogOperation.ADD);
        setIsEntityDialogOpen(false);
        setEntityTypeTab(CacheDataTypes.THING_TYPE);
      }}
      data-testid='history-add-dialog'
      entityId={currentEntityId}
      open={isEntityDialogOpen}
      operationType={entityDialogOperationType}
      setEntityId={setCurrentEntityId}
    />
  );

  useEffect(() => {
    const closeDrawer = (): void => {
      if (window.innerWidth > WIDTH_TO_CLOSE_DRAWER && window.innerHeight > HEIGHT_TO_CLOSE_DRAWER) return;
      handleDrawerToggle(false);
    };

    window.addEventListener('resize', closeDrawer);

    return () => window.removeEventListener('resize', closeDrawer);
  }, []);

  useEffect(() => {
  }, [storageKey]);

  return (
    <HistoryDrawerView
      desktopActions={desktopActions()}
      historyContent={renderInvestigationContent()}
      isDrawerOpen={isDrawerOpen}
      mobileActions={mobileActions()}
      mobileTabSelection={mobileTabSelection}
      renderAddEntityDialog={renderAddEntityDialog}
      renderDrawerTitle={renderDrawerTitle}
      renderOpenDrawerIcon={renderOpenDrawerIcon}
    />
  );
};
